import React, { useState, useEffect } from 'react';
import { Save, Check, Clock, GripVertical, Plus, X } from "lucide-react";
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { GlassButton } from './ui/GlassButton';
import { GlassSelect } from './ui/GlassSelect';
import './EditableContentCalendar.css';

interface CalendarRow {
  id: string;
  date: string;
  time: string;
  article: string;
  platforms: string;
  theme: string;
  notes: string;
}

interface EditableContentCalendarProps {
  tableData: any[];
  // May return `false` (sync or via Promise) to signal a refused save (e.g.
  // ContentPreview's wrapper guard) — the caller must not clear its dirty
  // state in that case. Returning void/true/undefined means "saved".
  onSave: (updatedData: any[]) => void | boolean | Promise<void | boolean>;
  taskId?: string;
  rowStatuses?: Record<string, string>;
  onRowStatusChange?: (article: string, status: 'pending' | 'approved') => void;
}

const EditableContentCalendar: React.FC<EditableContentCalendarProps> = ({
  tableData,
  onSave,
  taskId,
  rowStatuses,
  onRowStatusChange
}) => {
  const [rows, setRows] = useState<CalendarRow[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isReorderingDates, setIsReorderingDates] = useState(true);
  const [startDate, setStartDate] = useState<string>('');
  const [frequency, setFrequency] = useState<'weekly' | 'biweekly' | 'monthly'>('weekly');
  const [localStatuses, setLocalStatuses] = useState<Record<string, 'pending' | 'approved'>>({});

  // Initialize rows from table data
  useEffect(() => {
    const parsedRows = tableData.map((row, index) => ({
      id: `row-${index}`,
      date: row.date || row.Date || '',
      time: row.time || row.Time || '10:00',
      article: row.article || row.Article || '',
      platforms: row.platforms || row.Platforms || '',
      theme: row.theme || row.Theme || '',
      notes: row.notes || row.Notes || ''
    }));
    setRows(parsedRows);

    // Extract start date from first row
    if (parsedRows.length > 0 && parsedRows[0].date) {
      setStartDate(parsedRows[0].date);
    }
  }, [tableData]);

  // Sync local statuses from prop
  useEffect(() => {
    const mapped: Record<string, 'pending' | 'approved'> = {};
    if (rowStatuses) {
      for (const [key, val] of Object.entries(rowStatuses)) {
        mapped[key] = val === 'approved' ? 'approved' : 'pending';
      }
    }
    setLocalStatuses(mapped);
  }, [rowStatuses]);

  // Toggle approval status for a row (by article title)
  const handleToggleStatus = (article: string) => {
    const current = localStatuses[article] || 'pending';
    const newStatus: 'pending' | 'approved' = current === 'pending' ? 'approved' : 'pending';
    setLocalStatuses(prev => ({ ...prev, [article]: newStatus }));
    onRowStatusChange?.(article, newStatus);
  };

  // Calculate next date based on frequency
  const calculateNextDate = (currentDate: string, frequency: string): string => {
    const date = new Date(currentDate);
    if (isNaN(date.getTime())) return currentDate;

    switch (frequency) {
      case 'weekly':
        date.setDate(date.getDate() + 7);
        break;
      case 'biweekly':
        date.setDate(date.getDate() + 14);
        break;
      case 'monthly':
        date.setMonth(date.getMonth() + 1);
        break;
    }

    return date.toISOString().split('T')[0];
  };

  // Recalculate all dates starting from the first row
  const recalculateDates = (rows: CalendarRow[], startDate: string, frequency: string): CalendarRow[] => {
    if (!startDate || !isReorderingDates) return rows;

    let currentDate = startDate;
    return rows.map(row => {
      const updatedRow = { ...row, date: currentDate };
      currentDate = calculateNextDate(currentDate, frequency);
      return updatedRow;
    });
  };

  // Handle drag end - COMPREHENSIVE FIX VERSION
  const onDragEnd = (result: DropResult) => {
    console.log('🎯 Drag operation started:', result);

    if (!result.destination) {
      console.log('❌ No destination - dropped outside');
      return;
    }

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    console.log(`📍 Moving from ${sourceIndex} to ${destIndex}`);

    // If dropped in the same position, no change needed
    if (destIndex === sourceIndex) {
      console.log('⚡ Same position - no changes needed');
      return;
    }

    // Use functional update with proper React reconciliation
    setRows(currentRows => {
      console.log('🔄 Current rows before change:', currentRows.length);

      // Create a completely new array to ensure React sees the change
      const newRows = [...currentRows];

      // Remove from source position
      const [draggedItem] = newRows.splice(sourceIndex, 1);
      console.log('✂️ Removed item:', draggedItem.article?.substring(0, 30) || 'No article');

      // Insert at destination position
      newRows.splice(destIndex, 0, draggedItem);
      console.log('📌 Inserted at position:', destIndex);

      // Apply date recalculation if enabled
      const finalRows = isReorderingDates && startDate
        ? recalculateDates(newRows, startDate, frequency)
        : newRows;

      console.log('✅ Final row order:', finalRows.map((r, i) => `${i}: ${r.article?.substring(0, 20) || 'Empty'}`));

      // Trigger hasChanges in the same update cycle
      setTimeout(() => setHasChanges(true), 0);

      return finalRows;
    });

    console.log('🎉 Drag operation completed');
  };


  // Handle row edit
  const handleCellEdit = (rowId: string, field: keyof CalendarRow, value: string) => {
    setRows(prev => prev.map(row =>
      row.id === rowId ? { ...row, [field]: value } : row
    ));
    setHasChanges(true);

    // Update start date if first row's date changed
    if (field === 'date' && rows[0]?.id === rowId) {
      setStartDate(value);
    }
  };

  // Handle save
  const handleSave = async () => {
    // Convert back to original format (no approval_status — that's stored separately)
    const updatedData = rows.map(row => ({
      date: row.date,
      time: row.time,
      article: row.article,
      platforms: row.platforms,
      theme: row.theme,
      notes: row.notes
    }));
    const result = await onSave(updatedData);
    // A refused save (e.g. ContentPreview's wrapper guard) returns `false` —
    // leave hasChanges set so the editor still shows unsaved changes.
    if (result === false) {
      return;
    }
    setHasChanges(false);
  };

  // Handle add row
  const handleAddRow = (index: number) => {
    const newRow: CalendarRow = {
      id: `row-${Date.now()}`,
      date: '',
      time: '10:00',
      article: '',
      platforms: '',
      theme: '',
      notes: ''
    };

    const newRows = [...rows];
    newRows.splice(index + 1, 0, newRow);

    // Recalculate dates
    const updatedRows = isReorderingDates && startDate
      ? recalculateDates(newRows, startDate, frequency)
      : newRows;

    setRows(updatedRows);
    setHasChanges(true);
  };

  // Handle delete row
  const handleDeleteRow = (rowId: string) => {
    if (rows.length <= 1) return;

    const newRows = rows.filter(row => row.id !== rowId);

    // Recalculate dates
    const updatedRows = isReorderingDates && startDate
      ? recalculateDates(newRows, startDate, frequency)
      : newRows;

    setRows(updatedRows);
    setHasChanges(true);
  };

  return (
    <div className="editable-content-calendar">
      <div className="calendar-controls">
        <div className="control-group">
          <label>
            <input
              type="checkbox"
              checked={isReorderingDates}
              onChange={(e) => {
                setIsReorderingDates(e.target.checked);
                if (e.target.checked && startDate) {
                  setRows(recalculateDates(rows, startDate, frequency));
                  setHasChanges(true);
                }
              }}
            />
            Auto-adjust dates when reordering
          </label>
        </div>

        {isReorderingDates && (
          <div className="control-group frequency-selector">
            <label>Frequency</label>
            <GlassSelect
              size="sm"
              value={frequency}
              options={[
                { value: 'weekly',   label: 'Weekly' },
                { value: 'biweekly', label: 'Bi-weekly' },
                { value: 'monthly',  label: 'Monthly' },
              ]}
              onChange={(val) => {
                const newFreq = val as 'weekly' | 'biweekly' | 'monthly';
                setFrequency(newFreq);
                if (startDate) {
                  setRows(recalculateDates(rows, startDate, newFreq));
                  setHasChanges(true);
                }
              }}
            />
          </div>
        )}
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <table className="calendar-table editable">
          <thead>
            <tr>
              <th className="status-col">Status</th>
              <th className="drag-handle-col"></th>
              <th>Date</th>
              <th>Time</th>
              <th>Article</th>
              <th>Platforms</th>
              <th>Theme</th>
              <th>Notes</th>
              <th className="actions-col"></th>
            </tr>
          </thead>
          <Droppable droppableId="calendar-rows">
            {(provided) => (
              <tbody
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {rows.map((row, index) => {
                  const status = localStatuses[row.article] || 'pending';
                  const isApproved = status === 'approved';
                  return (
                    <Draggable key={row.id} draggableId={row.id} index={index}>
                      {(provided, snapshot) => (
                        <tr
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`${snapshot.isDragging ? 'dragging' : ''} ${isApproved ? 'row-approved' : 'row-pending'}`}
                        >
                          <td className="status-cell">
                            <button
                              className={`row-status-btn status-${status}`}
                              onClick={() => handleToggleStatus(row.article)}
                              title={isApproved ? 'Click to mark as Pending' : 'Click to mark as Approved'}
                              type="button"
                            >
                              {isApproved
                                ? <><Check size={11} />&nbsp;Approved</>
                                : <><Clock size={11} />&nbsp;Pending</>
                              }
                            </button>
                          </td>
                          <td
                            className="drag-handle"
                            title="Drag to reorder"
                            {...provided.dragHandleProps}
                          >
                            <GripVertical size={16} />
                          </td>
                          <td>
                            <input
                              type="date"
                              value={row.date}
                              onChange={(e) => handleCellEdit(row.id, 'date', e.target.value)}
                              className="cell-input"
                            />
                          </td>
                          <td>
                            <input
                              type="time"
                              value={row.time}
                              onChange={(e) => handleCellEdit(row.id, 'time', e.target.value)}
                              className="cell-input"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={row.article}
                              onChange={(e) => handleCellEdit(row.id, 'article', e.target.value)}
                              className="cell-input wide"
                              placeholder="Article title"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={row.platforms}
                              onChange={(e) => handleCellEdit(row.id, 'platforms', e.target.value)}
                              className="cell-input"
                              placeholder="blog, linkedin, etc."
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={row.theme}
                              onChange={(e) => handleCellEdit(row.id, 'theme', e.target.value)}
                              className="cell-input"
                              placeholder="Theme"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={row.notes}
                              onChange={(e) => handleCellEdit(row.id, 'notes', e.target.value)}
                              className="cell-input"
                              placeholder="Notes"
                            />
                          </td>
                          <td className="actions-cell">
                            <button
                              className="add-row-btn"
                              onClick={() => handleAddRow(index)}
                              title="Add row after"
                            >
                              <Plus size={14} />
                            </button>
                            <button
                              className="delete-row-btn"
                              onClick={() => handleDeleteRow(row.id)}
                              title="Delete row"
                              disabled={rows.length <= 1}
                            >
                              <X size={14} />
                            </button>
                          </td>
                        </tr>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </tbody>
            )}
          </Droppable>
        </table>
      </DragDropContext>

      <div className="calendar-actions">
        {hasChanges && (
          <span className="unsaved-indicator">Unsaved changes</span>
        )}
        <GlassButton
          variant="primary"
          onClick={handleSave}
          disabled={!hasChanges}
          icon={<Save size={16} />}
        >
          Save Calendar Changes
        </GlassButton>
      </div>
    </div>
  );
};

export default EditableContentCalendar;
